import { notify } from "@general-backoffice/core";
import { type StrapiId, type User, USER_POPULATE } from "@investhinks/api-client";

import { authEndpoints, usersEndpoints } from "../api";
import { impersonateUser as impersonateUserAction } from "../redux/reducers/session/actions";
import getErrorMessage from "./getErrorMessage";

type DispatchFn = (action: { type: string; payload?: unknown }) => unknown;
type NavigateFn = (path: string) => void;

/**
 * Start impersonating a user
 * @param userId - The ID of the user to impersonate
 * @param dispatch - Redux dispatch function
 * @param navigate - React Router navigate function (optional)
 * @returns True if successful, false otherwise
 */
export const startImpersonation = async (
  userId: StrapiId,
  dispatch: DispatchFn,
  navigate: NavigateFn | null = null
): Promise<boolean> => {
  try {
    // Call API to get impersonation token
    const {
      data: { jwt: impersonationToken },
    } = await authEndpoints.impersonateUser(userId);

    if (!impersonationToken) {
      throw new Error("Failed to retrieve impersonation token");
    }

    // Fetch target user details
    const { data: targetUser } = await usersEndpoints.getUser(userId, {
      populate: USER_POPULATE.ME,
    });

    if (!targetUser) {
      throw new Error("Failed to retrieve user details");
    }

    // Dispatch impersonate action to Redux
    dispatch(impersonateUserAction(impersonationToken, targetUser));

    notify(`Now viewing as ${targetUser.name || targetUser.email}`, "success");

    // Navigate to home page to refresh routes and menu for the impersonated user
    if (navigate) {
      navigate("/");
    }

    return true;
  } catch (err: unknown) {
    console.error("Error starting impersonation:", err);
    notify(getErrorMessage(err, "Failed to impersonate user. Please try again."), "error");
    return false;
  }
};

/**
 * Validate if a user can be impersonated (not an admin)
 * @param {object} user - User object to check
 * @returns {boolean} - True if user can be impersonated
 */
export const canImpersonateUser = (user: User | null | undefined): boolean => {
  if (!user || !user.role) return false;

  // Cannot impersonate admin users
  return user.role.name !== "Admin";
};
